<template>
  <div :id="data.id" class="bg-gray-lighten3 py-10 md:py-14">
    <div class="container overflow-hidden text-slate">
      <UiSectionTitle
        v-if="data.title"
        :title="data.title"
        class="title mb-6 flex whitespace-pre-line text-3xl uppercase leading-none text-slate md:justify-center md:text-center md:text-[3.5rem]"
      />

      <div
        v-if="data.text"
        class="mb-8 flex font-segoe text-md leading-[1.813rem] md:mb-12 md:justify-center md:text-center"
      >
        <span class="md:whitespace-pre-line" v-html="data.text" />
      </div>

      <div v-if="apps.length">
        <swiper-container
          ref="swiperEl"
          init="false"
          class="!overflow-visible pb-9 md:pb-0"
        >
          <div slot="container-end" class="swiper-scrollbar"></div>

          <swiper-slide
            v-for="(col, i) in apps"
            :key="i"
            class="flex flex-col gap-4"
          >
            <div
              v-for="(item, ii) in col"
              :key="`${i}_${ii}`"
              class="w-[241px] flex-grow rounded-lg bg-white p-6 text-sm sm:w-[324px] md:w-auto"
            >
              <div class="mb-2 flex min-h-[48px] items-center">
                <object
                  v-if="item.image"
                  :data="getImage(item.image)"
                  type="image/svg+xml"
                  class="mr-1 flex-shrink-0 flex-grow-0"
                ></object>

                <div
                  class="flex-grow-1 title uppercase leading-none tracking-[0.12em]"
                >
                  <span class="whitespace-pre-line" v-html="item.name" />
                </div>
              </div>

              <div class="leading-[1.225rem]" v-html="item.text" />
            </div>
          </swiper-slide>
        </swiper-container>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useMediaAsset, useSwiperElement } from '~/composables';

interface CategoryApp {
  id: string;
  name: string;
  text: string;
  image: string;
}
interface Props {
  data?: {
    title?: string;
    text?: string;
    items?: CategoryApp[];
  };
}

const props = withDefaults(defineProps<Props>(), {
  data: () => ({
    title: '',
    text: '',
    items: [],
  }),
});

const { getAssetUrl } = useMediaAsset();

const swiperOptions = {
  observer: true,
  slidesPerView: 'auto',
  spaceBetween: 16,
  breakpoints: {
    1024: {
      slidesPerView: 4,
    },
  },
  scrollbar: {
    el: '.swiper-scrollbar',
    draggable: true,
    dragSize: 180,
    snapOnRelease: true,
  },
};

const swiperEl = useSwiperElement(swiperOptions);

const apps = computed<CategoryApp[][]>(() => {
  return !props.data?.items
    ? []
    : props.data.items.reduce<CategoryApp[][]>((items, item, i) => {
        const chunkIndex = Math.floor(i / 2);

        items[chunkIndex] = items[chunkIndex] || [];
        items[chunkIndex].push(item);

        return items;
      }, []);
});

const getImage = (src: string) => getAssetUrl(src) ?? undefined;
</script>
